import { AxiosInstance } from "../../axios/axiosInstance";
import { apiRoutes } from "../../utils/ApiRoutes";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface IGetPhotoGalleryWidgetConfigPayload {
  business_id: string;
  widget_id?: string;
}

export interface IGetPhotoGalleryWidgetListPayload {
  business_id: string;
  widget_id?: string;
  cursor?: string | null;
  limit?: number;
  // Only read by the backend when widget_id is omitted (create mode, before the
  // widget's first save) - once widget_id exists these are ignored server-side
  // in favor of the saved widget doc.
  layout?: string;
  projects_config?: any;
  before_after_config?: any;
  // Bypasses layout-based resolution to always return the raw eligible/hand-picked
  // list, regardless of saved layout - the before_and_after builder sidebar passes
  // this to show every check-in to pick pairs from, not just already-paired ones.
  raw?: boolean;
  // Only applied server-side when raw is true - matches title/locality/area/month.
  search?: string;
}

export interface IGetPhotoGalleryWidgetJsonldPayload {
  business_id: string;
  widget_id: string;
}

export interface IAddPostImagePayload {
  post_id: string;
  url: string;
}

// ─── API Functions ────────────────────────────────────────────────────────────

/**
 * Create a brand-new photo gallery widget.
 * Required: checkin_id + widget_name. Everything else optional.
 */
export const addPhotoGalleryWidgetApi = async (payload: any) => {
  const response = await AxiosInstance.post(apiRoutes.addPhotoGalleryWidget, payload);
  return response;
};

/**
 * Update photo-gallery-kind-specific settings on an existing widget.
 * Required: widget_id.
 * For common fields (name, border_config, theme_config, cta_config, branding_config)
 * use editWidgetApi (shared endpoint) instead.
 */
export const editPhotoGalleryWidgetApi = async (payload: any) => {
  const response = await AxiosInstance.post(apiRoutes.editPhotoGalleryWidget, payload);
  return response;
};

/**
 * Bootstrap config for the dashboard editor / preview.
 * Returns { widget: <full normalized widget doc>, business_name, business_logo, logo_placement }.
 */
export const getPhotoGalleryWidgetConfigApi = async (
  payload: IGetPhotoGalleryWidgetConfigPayload,
) => {
  const response = await AxiosInstance.post(
    apiRoutes.getPhotoGalleryWidgetConfig,
    payload,
  );
  return response;
};

/**
 * Fetch paginated project posts / before-after pairs for the live widget.
 * Supports cursor-based pagination. Default limit: 12.
 */
export const getPhotoGalleryWidgetListApi = async (
  payload: IGetPhotoGalleryWidgetListPayload,
) => {
  const response = await AxiosInstance.post(
    apiRoutes.getPhotoGalleryWidgetList,
    payload,
  );
  return response;
};

/**
 * Fetch JSON-LD structured data for SEO.
 */
export const getPhotoGalleryWidgetJsonldApi = async (
  payload: IGetPhotoGalleryWidgetJsonldPayload,
) => {
  const response = await AxiosInstance.post(
    apiRoutes.getPhotoGalleryWidgetJsonld,
    payload,
  );
  return response;
};

const cleanFormDataFiles = (formData: any): any => {
  if (formData instanceof FormData) {
    const cleanFormData = new FormData();
    formData.forEach((value, key) => {
      if (value instanceof File) {
        const cleanName = value.name.replace(/\s+/g, "_");
        cleanFormData.append(key, value, cleanName);
      } else {
        cleanFormData.append(key, value);
      }
    });
    return cleanFormData;
  }
  return formData;
};

/**
 * Attach an image / file to an existing post's images array directly.
 * Accepts FormData (with post_id, file/image) or JSON payload ({ post_id, url }).
 */
export const addPostImageApi = async (payload: any) => {
  const isFormData = payload instanceof FormData;
  const processedPayload = isFormData ? cleanFormDataFiles(payload) : payload;
  const config = isFormData
    ? {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    : undefined;
  const response = await AxiosInstance.post(
    apiRoutes.addPostImage,
    processedPayload,
    config,
  );
  return response;
};
